<template>
	<tool-dialog
		icon="arrow-right"
		v-bind="$props"
		:searchable="false"
		:expandable="false"
		:display-name="activeTour.name"
		v-on="$listeners"
	>
		<div v-if="createTourStopMode === 'createButtons'" class="menu-container">
			<button class="scroll-button">
				<i class="tan-icon-arrow-right" />
			</button>
			<div>
				<button class="create-button" @click="startCanvasWorkflow">
					<i class="tan-icon-map" />{{ $t('tours.create-canvas') }}
				</button>
			</div>
			<button class="scroll-button">
				<i class="tan-icon-arrow-right" />
			</button>
		</div>
		<div v-else-if="createTourStopMode === 'canvas'" class="menu-container">
			<div class="menu-section">
				<ul class="aspects">
					<li
						v-for="(aspect, index) in aspects"
						:key="aspect.label"
						class="aspect"
						:class="{'disabled': aspect.disabled, 'selected': selectedAspectIndex === index}"
					>
						<i :class="'icon tan-icon-'+aspect.icon" />
						{{ $t('tours.'+aspect.label) }}
					</li>
				</ul>
				<div class="directions-and-buttons">
					<div class="choose">
						<i :class="'tan-icon-'+selectedAspect.icon" /><p class="choose-text">
							{{ $t('tours.choose-'+selectedAspect.label) }}
						</p>
					</div>
					<div>
						<b-button :disabled="selectedAspectIndex === 0" @click="back">
							{{ $t('tours.back') }}
						</b-button>
						<b-button class="ml-3" :disabled="isOnSaveStep" @click="next">
							{{ $t('tours.next') }}
						</b-button>
					</div>
				</div>
			</div>
			<div class="save menu-section">
				<i class="big-icon tan-icon-save" />
				<b-button v-b-modal.name-tour-stop variant="success" :disabled="!isOnSaveStep">
					{{ $t('tours.save-stop') }}
				</b-button>
			</div>
		</div>
		<div v-else-if="createTourStopMode === 'stops'" class="menu-container">
			<button class="scroll-button">
				<i class="tan-icon-arrow-right" />
			</button>
			<ol class="stops">
				<li v-for="(stop, index) in activeTour.data.stops" :key="stop.id">
					<b-form-select
						:ref="'stop-select-'+index"
						:value="index+1"
						:options="stopNumberOptions"
						@input="moveStop(index, $event - 1)"
					/>
					{{ stop.name }}
				</li>
			</ol>
			<button class="scroll-button">
				<i class="tan-icon-arrow-right" />
			</button>
		</div>
		<b-modal
			id="name-tour-stop"
			:title="$t('tours.name-stop')"
			@hidden="name = ''"
			@ok="createCanvasStop"
		>
			<form @submit.stop.prevent="createCanvasStop">
				<b-form-group :label="$t('tours.name')" label-for="stop-name">
					<b-form-input
						id="stop-name"
						v-model="name"
						trim
						autofocus
					/>
				</b-form-group>
			</form>
		</b-modal>
	</tool-dialog>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex';
import BaseTool from '@/components/Project/Tool/BaseTool.vue';

/*
Aspects array-of-objects extended to include function name to call saving state
within the CesiumViewer. 
*/
export default {
	name: 'CreateTourStop',
	extends: BaseTool,
	data() {
		return {
			aspects: [
				{ disabled: false, icon: 'home', label: 'basemap', czm: 'tourGetBasemap' },
				{ disabled: true, icon: 'map', label: 'overlays' },
				{ disabled: false, icon: 'save-screen', label: 'elements' },
				{ disabled: true, icon: 'sunlight', label: 'sun' },
				{ disabled: false, icon: 'optimize', label: 'data', call: 'tourOpenPanelProject' },
				{ disabled: true, icon: 'questionmark', label: 'infoboxes' },
				{ disabled: false, icon: 'create-new', label: 'camera', czm: 'tourGetCameraPosition' },
			],
			name: '',
			selectedAspectIndex: 0,
		};
	},
	computed: {
		...mapGetters(['activeTour', 'getTourViewProperties', 'getElementEditorEntityDataId']),
		...mapState(['createTourStopMode', 'openPanels']),
		isOnSaveStep() {
			return this.selectedAspectIndex === this.aspects.length;
		},
		selectedAspect() {
			if (this.isOnSaveStep) return { disabled: false, icon: 'save', label: 'save' };
			return this.aspects[this.selectedAspectIndex];
		},
		stopNumberOptions() {
			const options = [];
			for (let i = 1; i <= this.activeTour.data.stops.length; i++) {
				options.push(i);
			}
			return options;
		},
		tourLibraryIndex() {
			return this.openPanels.visualizationTools.slim1.tools.findIndex(
				(tool) => tool.componentName === 'TourLibrary'
			);
		},
		tourStopManagerIndex() {
			return this.openPanels.visualizationTools.slim1.tools.findIndex(
				(tool) => tool.componentName === 'TourStopManager'
			);
		},
	},
	methods: {
		...mapMutations([
			'closePanel',
			'addActiveTourStop',
			'moveActiveTourStop',
			'openPanel',
			'setCreateTourStopMode',
			'updateTourViewProperties',
		]),
		back() {
			do this.selectedAspectIndex--;
			while (this.selectedAspect.disabled);
		},
		tourOpenPanelProject() {
			this.updateTourViewProperties({
				projectTools: JSON.parse(JSON.stringify(this.openPanels.projectTools)),
				elementEditor: this.getElementEditorEntityDataId,
			});
		},
		createCanvasStop() {
			try {
				this.setCreateTourStopMode('stops');
				this.selectedAspectIndex = 0;
				this.addActiveTourStop({
					...this.getTourViewProperties,
					name: this.name,
					type: 'canvas',
				});
				this.axios.patch(`${this.$route.path}/tours/${this.activeTour.id}`, this.activeTour);
				if (this.tourLibraryIndex === -1)
					this.openPanel({
						area: 'visualizationTools',
						panel: { componentName: 'TourLibrary' },
						smartPanel: 'slim1',
					});
				if (this.tourStopManagerIndex === -1)
					this.openPanel({
						area: 'visualizationTools',
						panel: { componentName: 'TourStopManager' },
						smartPanel: 'slim1',
					});
				this.$bvModal.hide('name-tour-stop');
			} catch (error) {
				console.error(error);
			}
		},
		moveStop(oldIndex, newIndex) {
			this.moveActiveTourStop({ oldIndex, newIndex });
			this.$refs[`stop-select-${oldIndex}`].value = oldIndex;
		},
		next() {
			// call Cesium function name stored in aspect.czm
			if (this.aspects[this.selectedAspectIndex] && this.aspects[this.selectedAspectIndex].czm) {
				this.$czm(this.aspects[this.selectedAspectIndex].czm);
			}
			// call Local function name stored in aspect.call
			if (this.aspects[this.selectedAspectIndex] && this.aspects[this.selectedAspectIndex].call) {
				this[this.aspects[this.selectedAspectIndex].call]();
			}
			// move to next aspect, skipping the disabled
			do this.selectedAspectIndex++;
			while (this.selectedAspect.disabled);
		},
		startCanvasWorkflow() {
			this.setCreateTourStopMode('canvas');
			if (this.tourLibraryIndex > -1)
				this.closePanel({ area: 'visualizationTools', index: this.tourLibraryIndex, smartPanel: 'slim1' });
			if (this.tourStopManagerIndex > -1)
				this.closePanel({ area: 'visualizationTools', index: this.tourStopManagerIndex, smartPanel: 'slim1' });
		},
	},
};
</script>

<style scoped>
::v-deep .vdr {
	width: auto !important;
}

.menu-container {
	display: flex;
	align-items: center;
	justify-content: space-between;
}

.menu-section {
	padding: 1rem;
}

.aspects {
	display: flex;
	list-style: none;
	padding: 0;
	text-align: center;
}

.aspect {
	border: 1px solid transparent;
	width: 4.5rem;
}

.disabled {
	opacity: 0.5;
}

.selected {
	background: #aab3b7;
	border: 1px solid #333333;
	border-radius: 5px;
}

.icon {
	display: block;
	font-size: larger;
}

.directions-and-buttons {
	display: flex;
	align-items: center;
	justify-content: space-between;
}

.choose {
	font-style: italic;
}

.choose-text {
	display: inline;
}

.save {
	border-left: 1px #cccccc solid;
	width: min-content;
}

.big-icon {
	font-size: 4rem;
}

.stops {
	display: flex;
	list-style: none;
}
</style>